/*
 * The QR scanner (pages/scan/qr). The camera shows in a square; codes are read by the browser's own detector where
 * there is one (Chrome, Android), else by jsQR, loaded only here (iPhone, Firefox). A code with one of the site's
 * links opens it straight away (an older ticket's bare token too); anything else is shown as text (never as HTML) with
 * Open for a link and Copy. The
 * camera stops when the page is left or hidden, and starts again when it's back.
 */
import { ask, worked } from './permissions.js';   // a card before the browser asks for the camera

Alpine.data('qrScanner', (roots = [], ticket = null) => ({
    state: 'starting',      // starting, scanning, opening, denied, busy, unavailable
    result: null,           // {text, url}
    facing: 'environment',  // the back camera; Switch Camera turns to the front one and back
    switchable: false,      // more than one camera
    torchable: false,
    torch: false,
    stream: null,
    reader: null,
    timer: null,

    async init() {
        this.stop = this.stop.bind(this);
        this.wake = () => (document.hidden ? this.stop() : ['scanning', 'starting'].includes(this.state) && this.start());

        document.addEventListener('livewire:navigating', this.stop, {once: true});
        document.addEventListener('visibilitychange', this.wake);
        window.addEventListener('pagehide', this.stop);

        await this.start();
    },

    destroy() {
        this.stop();
        document.removeEventListener('visibilitychange', this.wake);
        window.removeEventListener('pagehide', this.stop);
    },

    // Opens the camera facing the way chosen; a camera that won't open (busy, or not that way round) is given a
    // moment and asked again more loosely, so switching never ends on "no camera"
    async open() {
        const tries = [{facingMode: {ideal: this.facing}}, {facingMode: this.facing}, true];

        for (const [index, video] of tries.entries()) {
            try {
                return await navigator.mediaDevices.getUserMedia({audio: false, video});
            } catch (error) {
                if (['NotAllowedError', 'SecurityError'].includes(error.name) || index === tries.length - 1) {
                    throw error;
                }
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        }
    },

    async start() {
        if (!navigator.mediaDevices?.getUserMedia) {
            this.state = 'unavailable';
            return;
        }

        this.stop();

        // Not Now on the card: the camera stays off, with Try Again
        if (!await ask('camera')) {
            this.state = 'declined';
            return;
        }

        this.state = 'starting';

        try {
            this.stream = await this.open();
        } catch (error) {
            this.state = ['NotAllowedError', 'SecurityError'].includes(error.name) ? 'denied'
                : error.name === 'NotReadableError' ? 'busy' : 'unavailable';
            return;
        }

        worked('camera');
        const video = this.$refs.video;
        video.srcObject = this.stream;
        await video.play().catch(() => {});

        const track = this.stream.getVideoTracks()[0];
        this.facing = track.getSettings().facingMode ?? this.facing;
        this.switchable = (await navigator.mediaDevices.enumerateDevices()).filter((device) => device.kind === 'videoinput').length > 1;
        this.torch = false;
        this.findTorch(track);

        this.reader ??= await this.makeReader();
        this.state = 'scanning';
        this.scan();
    },

    // Android tells whether the camera has a light only once it's running: asked straight away, again a moment
    // later, and through the photo settings (ImageCapture) as a last resort
    async findTorch(track) {
        for (const wait of [0, 500, 1500]) {
            await new Promise((resolve) => setTimeout(resolve, wait));
            if (track.readyState !== 'live') return;

            if (track.getCapabilities?.().torch) {
                this.torchable = true;
                return;
            }
        }

        try {
            const modes = (await new ImageCapture(track).getPhotoCapabilities()).fillLightMode ?? [];
            this.torchable = track.readyState === 'live' && modes.some((mode) => ['flash', 'torch'].includes(mode));
        } catch (e) {
            this.torchable = false;
        }
    },

    stop() {
        clearTimeout(this.timer);
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;
        this.torchable = false;
    },
    // Reads a code from the video: the browser's detector, or jsQR on a downsized frame
    async makeReader() {
        if ('BarcodeDetector' in window && (await BarcodeDetector.getSupportedFormats().catch(() => [])).includes('qr_code')) {
            const detector = new BarcodeDetector({formats: ['qr_code']});

            return async (video) => (await detector.detect(video))[0]?.rawValue ?? null;
        }

        const {default: jsQR} = await import('jsqr');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', {willReadFrequently: true});

        return async (video) => {
            const scale = Math.min(1, 640 / Math.max(video.videoWidth, video.videoHeight));
            canvas.width = Math.round(video.videoWidth * scale);
            canvas.height = Math.round(video.videoHeight * scale);
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            return jsQR(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height, {inversionAttempts: 'dontInvert'})?.data ?? null;
        };
    },

    // Five looks a second while scanning
    async scan() {
        if (this.state !== 'scanning' || !this.stream) return;

        const video = this.$refs.video;
        const text = video.readyState >= 2 ? await this.reader(video).catch(() => null) : null;

        if (text && text !== this.result?.text) {
            this.found(text);
        }

        this.timer = setTimeout(() => this.scan(), 200);
    },

    found(text) {
        navigator.vibrate?.(40);

        // An older ticket's QR code holds just its token (a UUID); newer ones hold its short link
        if (ticket && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) {
            text = ticket + text;
        }

        let url = null;
        try {
            const parsed = new URL(text);
            url = ['http:', 'https:'].includes(parsed.protocol) ? parsed : null;
        } catch (e) {}

        // One of the site's own links: open it
        if (url && roots.some((root) => url.hostname === root || url.hostname.endsWith('.' + root))) {
            this.state = 'opening';
            this.stop();
            location.assign(url.href);
            return;
        }

        this.result = {text, url: url?.href ?? null};
    },

    async flip() {
        this.facing = this.facing === 'user' ? 'environment' : 'user';
        await this.start();
    },

    async light() {
        const track = this.stream?.getVideoTracks()[0];
        if (!track) return;

        this.torch = !this.torch;
        await track.applyConstraints({advanced: [{torch: this.torch}]}).catch(() => (this.torch = false));
    },

    async copy() {
        try {
            await navigator.clipboard.writeText(this.result.text);
            window.Toaster?.success('Copied.');
        } catch (e) {
            window.Toaster?.error('Couldn\'t copy it.');
        }
    },
}));
